/**
 * City hubs `/{cc}/{lang}/city/:city` (web plan W3.2, master D-5):
 * the rules the SPA page (src/pages/CityPage.tsx) and the SEO server
 * (server/routes.mjs) share, so both read the backend's `cityPage` (B3.7b)
 * the same way.
 *
 * - One slug per city: `/city/{city}`. Which slugs exist is the backend's
 *   answer: an unknown slug, an inactive city, or another country's city is
 *   NOT_FOUND there, a 404 here.
 * - An old slug (renamed city, merged city) gets a 301 from the page's own
 *   `urlPath` (the canonical; the server and the SPA both redirect to it).
 * - Robots come only from the backend's `seo.robots`.
 * - Another hub is linked from a hub only while that hub's own
 *   `seo.indexable` is true (D-5): a hub under its threshold, or one the
 *   backend doesn't list yet, is never linked from other hubs.
 * - `?page=N` pages the live sales, LIST_PAGE_SIZE a page, like the category
 *   hubs (web plan W3.1).
 *
 * Pure (no React, no `window`).
 */
import type { Brand, Category, EntitySeo, Promotion } from '@/types';
import { localeKey, type Locale } from './locales';
import { cleanName } from './text';
import { LIST_PAGE_SIZE, lastListPage } from './urls';

/** A city reference as the backend returns it. */
export interface CityRef {
  id: string;
  name: string;
  slug: string;
  /** The city's hub path (`/pk/en/city/{slug}`); null while no locale is launched for its country. */
  urlPath?: string | null;
}

/** One brand on the city hub. */
export interface CityBrandRow {
  brand: Brand & { seo?: EntitySeo | null };
  /** Its publicly visible stores in the city. */
  storeCount: number;
  /**
   * Its brand × city page (`/pk/en/brand/{brand}/{city}`) when that page is
   * indexable (≥ 3 stores); null = link the brand hub instead (`brand.urlPath`).
   */
  brandCityPath?: string | null;
}

/** A category hub the city's brands are on. */
export interface CityCategoryRow {
  /** An indexable level 1–2 hub the city's brands are tagged on. */
  category: Category & { seo?: EntitySeo | null };
  /** How many of the city's brands are tagged with it or below it. */
  brandCount: number;
}

/** A nearby city on the city hub. */
export type NearbyCity = CityRef & { seo?: EntitySeo | null };

/** The backend's `cityPage` answer, as the SPA and the server select it (`CITY_HUB_PAGE`). */
export interface CityHubPage {
  /** The hub's canonical path: serve only there, 301 to it from any other spelling. */
  urlPath: string;
  city: CityRef;
  /** Active now, valid at a store in the city; local promotions first. */
  livePromotions: readonly Promotion[];
  upcomingPromotions: readonly Promotion[];
  /** A–Z by brand name. */
  brands: readonly CityBrandRow[];
  /** Count of visible brands with visible stores in the city (D-5 threshold is 20). */
  brandCount: number;
  /** Indexable level 1–2 category hubs the city's brands are on. */
  categories: readonly CityCategoryRow[];
  /** Up to 8 other indexable cities nearby. */
  nearbyCities: readonly NearbyCity[];
  /** The backend's index decision for this hub (D-5). */
  seo: EntitySeo;
}

/** One route segment: decoded, trimmed, lower case; null when not a usable slug. */
function hubSegment(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }
  value = value.trim().toLowerCase();
  // A slug is one path segment: no separators, no whitespace or control characters.
  if (!value || /[/\\?#\s\p{Cc}]/u.test(value)) return null;
  return value;
}

/**
 * The backend's `cityPage(slug)` key for the route's `:city` segment.
 * Null for segments no city slug can have: the page is a 404 without asking
 * the backend.
 */
export function cityHubKey(city: string | null | undefined): string | null {
  return hubSegment(city);
}

/** `cityPage(slug, locale)` variables: the one place the SPA and the SEO server build them. */
export function cityPageVariables(key: string, locale: Locale): { slug: string; locale: string } {
  return { slug: key, locale: localeKey(locale) };
}

/**
 * The nav cities query's variables (`NavCities`: the footer, the home city
 * block, /nearby's city links) for `locale`: built here for the SPA's
 * `useNavCities` and the SEO server's page data alike (web plan W4.5).
 */
export function navCitiesVariables(locale: Locale): { country: string; locale: string } {
  return { country: locale.cc.toUpperCase(), locale: localeKey(locale) };
}

/** The city's display name for titles and H1s: cleaned (no leading/trailing whitespace, safe). */
export function cityHubName(city: Pick<CityRef, 'name'>): string {
  return cleanName(city.name) || city.name;
}

// The D-5 link rule lives in a dependency-free module the footer can import.
export { linkedFromHubs, sitePath } from './hubLinks';

/** The hub's last `?page=` (live sales; an empty list still has page 1). */
export function cityHubLastPage(hub: Pick<CityHubPage, 'livePromotions'>): number {
  return lastListPage(hub.livePromotions.length);
}

/** The live sales page `page` shows. */
export function cityHubPromotionsOn(
  hub: Pick<CityHubPage, 'livePromotions'>,
  page: number,
): readonly Promotion[] {
  const n = Number.isSafeInteger(page) && page >= 1 ? page : 1;
  return hub.livePromotions.slice((n - 1) * LIST_PAGE_SIZE, n * LIST_PAGE_SIZE);
}
